import React, { useState } from 'react';
import { InventoryItem } from '../types';
import { exportCsv } from '../lib/changeLog';

interface Props {
  items: InventoryItem[];
  onAddItem: (item: InventoryItem) => void;
  onUpdateItem: (item: InventoryItem) => void;
  onDeleteItem: (id: string) => void;
}

const emptyForm = {
  name: '', sku: '', category: 'Equipment' as InventoryItem['category'],
  quantity: 0, unit: 'each', unitCost: 0, supplier: '', location: '',
  reorderPoint: 0, notes: '',
};

const CATEGORIES: InventoryItem['category'][] = ['Equipment', 'Fuel', 'Climbing Gear', 'Chainsaw Parts', 'PPE', 'Chemicals', 'Office', 'Other'];

const statusOf = (item: InventoryItem): InventoryItem['status'] => {
  if (item.quantity <= 0) return 'OUT_OF_STOCK';
  if (item.reorderPoint > 0 && item.quantity <= item.reorderPoint) return 'LOW';
  return 'IN_STOCK';
};

const InventoryView: React.FC<Props> = ({ items, onAddItem, onUpdateItem, onDeleteItem }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [error, setError] = useState('');

  const visible = items.filter(i => !i.isDeleted);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setError('');
    setShowForm(true);
  };
  const openEdit = (item: InventoryItem) => {
    setEditing(item);
    setForm({
      name: item.name, sku: item.sku, category: item.category,
      quantity: item.quantity, unit: item.unit, unitCost: item.unitCost,
      supplier: item.supplier, location: item.location,
      reorderPoint: item.reorderPoint, notes: item.notes || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Item name is required.'); return; }
    if (form.quantity < 0) { setError('Quantity cannot be negative.'); return; }

    if (editing) {
      const updated: InventoryItem = {
        ...editing,
        ...form,
        status: statusOf({ ...editing, ...form }),
        updatedAt: new Date().toISOString(),
      };
      onUpdateItem(updated);
    } else {
      const now = new Date().toISOString();
      const item: InventoryItem = {
        ...form,
        id: '', // assigned by Firestore addDoc
        status: statusOf({ ...form } as InventoryItem),
        createdAt: now,
        updatedAt: now,
      };
      onAddItem(item);
    }
    setShowForm(false);
    setForm({ ...emptyForm });
    setError('');
  };

  const handleDelete = (item: InventoryItem) => {
    if (!window.confirm(`Delete "${item.name}" from inventory? This is a soft delete and can be restored by an admin.`)) return;
    onDeleteItem(item.id);
  };

  const doExport = () => {
    exportCsv(
      `inventory-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Name', 'SKU', 'Category', 'Quantity', 'Unit', 'Unit Cost', 'Total Value', 'Supplier', 'Location', 'Reorder Point', 'Status'],
      visible.map(i => [
        i.name, i.sku, i.category, i.quantity, i.unit, i.unitCost,
        +(i.quantity * i.unitCost).toFixed(2), i.supplier, i.location, i.reorderPoint, statusOf(i),
      ])
    );
  };

  const totalValue = visible.reduce((s, i) => s + i.quantity * i.unitCost, 0);
  const lowCount = visible.filter(i => statusOf(i) === 'LOW').length;
  const outCount = visible.filter(i => statusOf(i) === 'OUT_OF_STOCK').length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-bark">Inventory & Supplies</h2>
          <p className="text-stone-500 text-sm mt-1">Track equipment, fuel, gear and materials. Changes are logged and exportable for verification.</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={doExport} className="px-4 py-2 bg-stone-100 text-stone-600 rounded-lg font-bold text-sm hover:bg-stone-200 transition">Export CSV</button>
          <button onClick={openAdd} className="px-4 py-2 bg-palm text-white rounded-lg font-bold text-sm hover:bg-gecko transition shadow-sm">+ Add Item</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Total Items</p>
          <h3 className="text-3xl font-black text-gecko mt-1">{visible.length}</h3>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Total Value</p>
          <h3 className="text-3xl font-black text-palm mt-1">${totalValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</h3>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Needs Restock</p>
          <h3 className="text-3xl font-black text-orange-500 mt-1">{lowCount} low · {outCount} out</h3>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Item</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Category</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Qty</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Unit Cost</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest">Location</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest text-center">Status</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-stone-400 tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {visible.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-16 text-center text-stone-400 font-bold italic">No inventory items yet. Click "Add Item" to start tracking.</td></tr>
              )}
              {visible.map(item => {
                const st = statusOf(item);
                return (
                  <tr key={item.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-bark">{item.name}</p>
                      {item.sku && <p className="text-[10px] font-mono text-stone-400 uppercase mt-0.5">{item.sku}</p>}
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">{item.category}</td>
                    <td className="px-6 py-4 text-sm font-bold text-right">{item.quantity} {item.unit}</td>
                    <td className="px-6 py-4 text-sm text-right">${item.unitCost.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-stone-600">{item.location || '—'}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                        st === 'IN_STOCK' ? 'bg-gecko/15 text-gecko' :
                        st === 'LOW' ? 'bg-orange-100 text-orange-600' : 'bg-red-100 text-red-600'
                      }`}>{st.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button onClick={() => openEdit(item)} className="px-3 py-1.5 bg-stone-100 text-stone-600 text-xs font-bold rounded-lg hover:bg-stone-200 transition">Edit</button>
                        <button onClick={() => handleDelete(item)} className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-bold rounded-lg hover:bg-red-100 transition">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-palm p-6 text-white">
              <h3 className="text-xl font-bold">{editing ? 'Edit Inventory Item' : 'Add Inventory Item'}</h3>
              <p className="text-palm-100 text-sm opacity-80 italic">Saved changes persist and appear in the change log.</p>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs font-bold">{error}</div>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Name *</label>
                  <input required type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" placeholder="e.g. Climbing Rope 150ft" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">SKU</label>
                  <input type="text" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" placeholder="e.g. ROPE-150" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Category</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value as InventoryItem['category'] })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Location</label>
                  <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" placeholder="e.g. Shop A" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Quantity</label>
                  <input type="number" min={0} value={form.quantity} onChange={e => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Unit</label>
                  <input type="text" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" placeholder="each" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Unit Cost $</label>
                  <input type="number" min={0} step="0.01" value={form.unitCost} onChange={e => setForm({ ...form, unitCost: parseFloat(e.target.value) || 0 })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Supplier</label>
                  <input type="text" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" placeholder="e.g. Treestuff.com" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Reorder Point</label>
                  <input type="number" min={0} value={form.reorderPoint} onChange={e => setForm({ ...form, reorderPoint: parseInt(e.target.value) || 0 })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none resize-none" placeholder="Optional notes" />
              </div>
              <div className="flex space-x-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 text-stone-500 font-bold hover:bg-stone-100 rounded-lg transition">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-gecko text-white font-bold rounded-lg hover:bg-palm transition shadow-lg shadow-gecko/20">{editing ? 'Save Changes' : 'Create Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryView;
